/**
 * Per-session in-memory state: the storage encryption key, and the staging map used before that
 * key is available.
 *
 * These were module-level singletons (`storageKeyHolder` and `internalStorage` in Utility.js). With
 * two accounts live in one page that meant one AEAD key for both, so account A's entries could be
 * decrypted with account B's key. Keying them by sessionId is the fix.
 *
 * Contexts live for the lifetime of the page and are never evicted. The server derives the storage
 * key from a device fingerprint that includes screen dimensions, so a re-fetch after the window
 * moves to another monitor returns a *different* key and previously encrypted entries stop
 * decrypting. Holding the key for the page's lifetime is what keeps that latent problem latent.
 * Memory is bounded by MAX_SIGNED_IN_SESSIONS instead. See
 * docs/multi-account-session-design.md, decision D2.
 */

const contexts = new Map();

const requireSessionId = (sessionId) => {
  if (!sessionId) {
    // Deliberately fatal. Falling back to a shared bucket is exactly the singleton behaviour this
    // module exists to remove, and it fails as a wrong-key decryption far from the missing argument.
    throw new Error("sessionId is required to access session context.");
  }
  return sessionId;
};

const contextFor = (sessionId) => {
  const id = requireSessionId(sessionId);
  let context = contexts.get(id);
  if (!context) {
    context = { storageKey: null, internalStorage: null };
    contexts.set(id, context);
  }
  return context;
};

export const getStorageKeyFor = (sessionId) => contextFor(sessionId).storageKey;

export const setStorageKeyFor = (sessionId, storageKey) => {
  contextFor(sessionId).storageKey = storageKey;
};

export const hasInternalStorage = (sessionId) =>
  contextFor(sessionId).internalStorage != null;

export const getInternalStorageValue = (sessionId, identifier) => {
  const { internalStorage } = contextFor(sessionId);
  if (!internalStorage) {
    return null;
  }
  const value = internalStorage.get(identifier);
  return value === undefined ? null : value;
};

export const setInternalStorageValue = (sessionId, identifier, value) => {
  const context = contextFor(sessionId);
  if (!context.internalStorage) {
    context.internalStorage = new Map();
  }
  context.internalStorage.set(identifier, value);
};

/**
 * The staging map itself, for the flush that encrypts every pending entry once the storage key
 * arrives. Returns null when nothing is staged.
 */
export const getInternalStorageMap = (sessionId) => contextFor(sessionId).internalStorage;

/**
 * Drops one session's key material.
 *
 * JavaScript strings cannot be wiped, so this releases the references and relies on garbage
 * collection. It is not a secure erase and should not be described as one.
 */
export const clearContext = (sessionId) => {
  if (!sessionId) {
    return;
  }
  contexts.delete(sessionId);
};

export const clearAllContexts = () => {
  contexts.clear();
};

/** Test seam. Not part of the SDK's public surface. */
export const activeContextSessionIds = () => Array.from(contexts.keys());
